import React, { useState } from "react";
import { SCHEMA } from "../data/reportSchema";
import { useReportHistory } from "../hooks/useReportHistory";
import { useDraftSave } from "../hooks/useDraftSave";
import { generateReport } from "../api/llm_api";

import CopilotSidebar from "../components/copilot/CopilotSidebar";
import ReportCanvas from "../components/copilot/ReportCanvas";
import ReportHistoryPanel from "../components/history/ReportHistoryPanel";

export default function CopilotWorkspace() {
  // ── Core State ──────────────────────────────────────────
  const [selectedScenario, setSelectedScenario] = useState("Custom_Issue");
  const [formData, setFormData] = useState({});
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  
  const { history, addReport, deleteReport, clearHistory } = useReportHistory();
  const { saveDraft, loadDraft, clearDraft, hasDraft } = useDraftSave();

  // ── Handlers ──────────────────────────────────────────
  const handleSelectScenario = (scenarioKey) => {
    setSelectedScenario(scenarioKey);
    setReportData(null);

    const draft = loadDraft(scenarioKey);
    if (draft && draft.formData) {
      setFormData(draft.formData);
    } else {
      setFormData({});
    }
  };

  const handleFormChange = (id, value) => {
    setFormData((prev) => {
      const next = { ...prev, [id]: value };
      saveDraft(selectedScenario, next);
      return next;
    });
  };

  const handleGenerate = async () => {
    if (!selectedScenario) return;
    setIsLoading(true);
    setReportData(null);
    try {
      const result = await generateReport(selectedScenario, formData);
      setReportData(result);
      addReport({
        scenarioKey: selectedScenario,
        title: result.report_title || "제목 없음",
        summary: result.executive_summary?.slice(0, 100) || "요약 없음",
        formData,
        reportData: result,
      });
      clearDraft(selectedScenario);
    } catch (err) {
      console.error(err);
      alert("생성 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const loadFromHistory = (item) => {
    setSelectedScenario(item.scenarioKey);
    setFormData(item.formData);
    setReportData(item.reportData);
    setHistoryPanelOpen(false);
  };

  const resetWorkspace = () => {
    setSelectedScenario("Custom_Issue");
    setFormData({});
    setReportData(null);
  };

  // ── Render ──────────────────────────────────────────
  return (
    <div className="flex h-screen overflow-hidden bg-slate-950">
      {/* ── Left Pane: Copilot AI Input ── */}
      <div className="w-[450px] flex-shrink-0 border-r border-[rgba(255,255,255,0.08)] bg-[rgba(15,23,42,0.6)] backdrop-blur-3xl z-10 shadow-2xl flex flex-col relative">
        <CopilotSidebar 
          selectedScenario={selectedScenario}
          onSelectScenario={handleSelectScenario}
          formData={formData}
          onChangeForm={handleFormChange}
          onGenerate={handleGenerate}
          isLoading={isLoading}
          onReset={resetWorkspace}
          onOpenHistory={() => setHistoryPanelOpen(true)}
        />
      </div>

      {/* ── Right Pane: Live Report Canvas ── */}
      <div className="flex-1 relative overflow-auto custom-scrollbar bg-transparent">
        <ReportCanvas 
          reportData={reportData} 
          isLoading={isLoading} 
          scenarioInfo={selectedScenario ? SCHEMA.scenario_router.all_scenarios[selectedScenario] : null}
        />
      </div>

      {/* 히스토리 모달 */}
      <ReportHistoryPanel
        isOpen={historyPanelOpen}
        onClose={() => setHistoryPanelOpen(false)}
        history={history}
        onLoad={loadFromHistory}
        onDelete={deleteReport}
        onClearAll={clearHistory}
      />
    </div>
  );
}
